import { Shield, Landmark, Target, Search } from "lucide-react";
import {
  FadeIn, FadeInX, StaggerContainer, StaggerItem,
  RevealLine, PageTransition, HoverCard,
} from "@/components/site/Animations";
import { SectionHeader } from "@/components/site/SectionHeader";
import { ContactBanner } from "@/components/site/ContactBanner";
import { Separator } from "@/components/ui/separator";

const CAPABILITIES = [
  {
    icon: Search,
    title: "Strategic Sourcing & Partner Architecture",
    description: "Identify, qualify, and structure relationships with suppliers, integrators, and technical partners across domestic and cross-border supply chains.",
    details: [
      "Identify and qualify suppliers, integrators, and technical partners",
      "Structure relationships for accountability — defined scopes, enforceable milestones, schedules built for disruption",
      "Domestic and cross-border sourcing across regulated environments",
      "Partner teaming structures aligned to program requirements",
    ],
    outcomes: "Accountable supply networks ready for program execution.",
  },
  {
    icon: Landmark,
    title: "Government Engagement & Stakeholder Navigation",
    description: "Provide a disciplined, credible interface with government departments, regulators, and decision-makers throughout the procurement lifecycle.",
    details: [
      "Credible interface with government departments, regulators, and decision-makers",
      "Translate technical requirements into clear, actionable messaging",
      "Multi-agency coordination and oversight management",
      "Rapid-response communications and stakeholder alignment during program shifts",
    ],
    outcomes: "Aligned stakeholders with clear channels and responsive communications.",
  },
  {
    icon: Target,
    title: "Capture Support & Procurement Readiness",
    description: "Position programs for pursuit with teaming strategy, compliance inputs, and proposal-ready artifacts aligned to government acquisition processes.",
    details: [
      "Program positioning and teaming strategy development",
      "Risk registers with accountable mitigation owners",
      "Pricing and schedule assumptions for defensible proposals",
      "Compliance inputs and proposal-ready artifacts aligned to acquisition processes",
    ],
    outcomes: "Pursuit-ready posture with defensible assumptions and structured risk.",
  },
  {
    icon: Shield,
    title: "Technical Diligence & Market Intelligence",
    description: "Convert fragmented information into actionable insight — supporting decisions on sourcing, deployment, and supply chain viability for emerging technologies.",
    details: [
      "Convert fragmented information into actionable feasibility and risk insight",
      "Support decisions on sourcing, deployment, and supply chain viability",
      "Deep technical diligence for novel systems and emerging technology procurements",
      "Strategic perspective combined with execution discipline",
    ],
    outcomes: "Actionable intelligence for confident procurement decisions.",
  },
];

export default function CapabilitiesPage() {
  return (
    <PageTransition>
      <div data-testid="capabilities-page">
        {/* Page Header */}
        <section className="bg-white pt-10 sm:pt-12 lg:pt-16 pb-10">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <FadeIn>
              <SectionHeader
                eyebrow="What We Do"
                title="Capabilities"
                description="Cortes Industries delivers structured operational services across the procurement lifecycle — from capture through delivery. Each capability is built around accountability, risk management, and mission-focused execution."
              />
            </FadeIn>
          </div>
        </section>

        <RevealLine />

        {/* Capabilities List */}
        <section className="py-14 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <div className="space-y-12" data-testid="capabilities-accordion">
              {CAPABILITIES.map((cap, idx) => (
                <div key={cap.title}>
                  <FadeIn delay={idx * 0.08}>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                      <div className="lg:col-span-5">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0 mt-0.5">
                            <cap.icon className="w-5 h-5 text-[hsl(41,47%,61%)]" />
                          </div>
                          <div>
                            <h3
                              className="text-xl font-semibold tracking-tight text-foreground"
                              style={{ fontFamily: 'var(--font-display)' }}
                              data-testid={`capability-card-${cap.title.split(' ')[0].toLowerCase()}`}
                            >
                              {cap.title}
                            </h3>
                            <p className="text-sm leading-relaxed text-muted-foreground mt-2">
                              {cap.description}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="lg:col-span-7">
                        <ul className="space-y-3">
                          {cap.details.map((detail) => (
                            <li key={detail} className="flex items-start gap-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                              <p className="text-sm leading-relaxed text-muted-foreground">{detail}</p>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-5 p-4 bg-secondary/60 rounded-lg border border-border">
                          <p className="text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground mb-1" style={{ fontFamily: 'var(--font-display)' }}>
                            Typical Outcome
                          </p>
                          <p className="text-sm text-foreground">{cap.outcomes}</p>
                        </div>
                      </div>
                    </div>
                  </FadeIn>
                  {idx < CAPABILITIES.length - 1 && <Separator className="mt-12" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement Models */}
        <section className="py-14 sm:py-16 lg:py-20 bg-secondary/40">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <FadeIn>
              <SectionHeader
                eyebrow="How We Work"
                title="Engagement Models"
                description="Engagements are structured around operational impact, not advisory volume."
                className="mb-10"
              />
            </FadeIn>
            <StaggerContainer stagger={0.1} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { title: "Operator or Advisory Support", desc: "From capture through delivery — hands-on execution with P&L accountability." },
                { title: "Targeted Sourcing", desc: "Qualification for sensitive or restricted supply chains across regulated environments." },
                { title: "Technical Diligence", desc: "Focused diligence and market intelligence on emerging technology procurements." },
                { title: "Rapid-Response Alignment", desc: "Communications and stakeholder alignment during program shifts or external scrutiny." },
              ].map((model) => (
                <StaggerItem key={model.title}>
                  <div className="border border-border rounded-lg p-6 bg-white card-hover h-full">
                    <h3 className="text-base font-semibold tracking-tight text-foreground mb-2" style={{ fontFamily: 'var(--font-display)' }}>
                      {model.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{model.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        <ContactBanner />
      </div>
    </PageTransition>
  );
}
